import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { androidColors } from '../../theme/androidTheme';
import Layout from '../../components/Layout/Layout';
import PageTitle from '../../components/PageTitle/PageTitle';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const MARKET_API_URL = 'https://market-cryptonvx.market-api.workers.dev/v1/market-insights';

const Market: React.FC = () => {
  const [marketData, setMarketData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    setLoading(true);
    fetch(MARKET_API_URL)
      .then(res => res.json())
      .then(setMarketData)
      .catch(() => setError(t('common.fetchError') || 'Erreur lors de la récupération des données'))
      .finally(() => setLoading(false));
  }, []);

  // Helper pour la couleur des signaux
  const getSignalColor = (signal: string) => {
    switch (signal) {
      case 'Strong Buy': return androidColors.success;
      case 'Buy': return androidColors.success;
      case 'Neutre': return androidColors.textSecondary;
      case 'Sell': return androidColors.error;
      case 'Strong Sell': return androidColors.error;
      default: return androidColors.textSecondary;
    }
  };

  // Helper pour l'icône des signaux
  const getSignalIcon = (signal: string) => {
    switch (signal) {
      case 'Strong Buy':
      case 'Buy':
        return <TrendingUp sx={{ color: getSignalColor(signal) }} />;
      case 'Sell':
      case 'Strong Sell':
        return <TrendingDown sx={{ color: getSignalColor(signal) }} />;
      default:
        return <TrendingFlat sx={{ color: getSignalColor(signal) }} />;
    }
  };

  // Helper pour l'icône du signal global (Signal actuel)
  const getGlobalSignalIcon = (signal: string) => {
    switch (signal) {
      case 'Buy':
        return <TrendingUp fontSize="medium" sx={{}} />;
      case 'Sell':
        return <TrendingDown fontSize="medium" sx={{}} />;
      default:
        return <TrendingFlat fontSize="medium" sx={{}} />;
    }
  };

  // Helper pour la couleur Fear & Greed
  const getFearGreedColor = (value: number) => {
    if (value >= 75) return androidColors.success;
    if (value >= 50) return androidColors.primaryGold;
    if (value >= 25) return androidColors.warning;
    return androidColors.error;
  };

  // Helper pour la couleur de variation 24h
  const getVariationColor = (variation: string) => {
    const v = Number(variation);
    if (v === 0) return '#fff';
    if (v < 0) return androidColors.error;
    return androidColors.success;
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress sx={{ color: androidColors.primaryGold }} /></Box>;
  if (error || !marketData) return <Alert severity="error">{error || t('common.unknownError')}</Alert>;

  // Extraction des données avec fallback
  const fear_and_greed = marketData.fear_and_greed || {};
  const moving_averages = marketData.moving_averages || {};
  const next_halving = marketData.next_halving || {};
  const btcMA = moving_averages.bitcoin || {};

  // Pour le compte à rebours du halving
  const now = new Date();
  const halvingDate = next_halving.est_date ? new Date(next_halving.est_date) : null;
  const diff = halvingDate ? (halvingDate.getTime() - now.getTime()) : 0;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const timeToHalving = halvingDate ? `${days} ${t('market.days')} ${t('market.and')} ${hours} ${t('market.hours')}` : t('market.unavailable');

  // Mapping pour l'affichage des MA avec couleurs/icônes fixes comme dans la maquette d'origine
  const maBlocks = [
    { key: 'MA10', label: 'MA10' },
    { key: 'MA20', label: 'MA20' },
    { key: 'MA50', label: 'MA50' },
    { key: 'MA100', label: 'MA100' },
    { key: 'MA200', label: 'MA200' },
  ];

  return (
    <Layout title={t('market.title')}>
      <PageTitle title={t('market.title')} />
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Stack spacing={3}>
            {/* Moyennes Mobiles */}
            <Card sx={{ backgroundColor: androidColors.cardBackground, border: `1px solid ${androidColors.borderColor}`, borderRadius: '12px' }}>
              <CardContent>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: androidColors.textColor, mb: 1 }}>
                    {t('market.movingAverages')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
                    {t('market.movingAveragesDesc')}
                  </Typography>
                </Box>
                {/* Résumé global */}
                {btcMA.signal ? (
                  <Box sx={{
                  backgroundColor: 'rgba(241, 192, 134, 0.063)', // couleur par défaut
                    borderRadius: '12px',
                    p: 2,
                    mb: 3,
                  border: '1px solid #F1C08630',
                }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
                      <Box sx={{ 
                      backgroundColor: getSignalColor(btcMA.signal),
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                      {getGlobalSignalIcon(btcMA.signal)}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: androidColors.textSecondary, mb: 0.5 }}>
                          Signal actuel
                        </Typography>
                      <Typography variant="h5" sx={{ color: getSignalColor(btcMA.signal), fontWeight: 'bold' }}>
                        {btcMA.signal}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ) : (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    {t('market.globalSignalUnavailable')}
                  </Alert>
                )}
                {/* Liste des signaux par période */}
                <Stack spacing={2}>
                {maBlocks.map(({ key, label }) => (
                  btcMA[key] && btcMA[key].signal ? (
                    <Box key={key} sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      borderRadius: '8px',
                      backgroundColor: `${getSignalColor(btcMA[key]?.signal)}10`,
                      border: `1px solid ${getSignalColor(btcMA[key]?.signal)}30`
                    }}>
                      <Typography variant="body1" sx={{ color: androidColors.textColor, fontWeight: 500 }}>
                        {label}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {getSignalIcon(btcMA[key]?.signal)}
                        <Typography sx={{ color: getSignalColor(btcMA[key]?.signal), fontWeight: 500 }}>
                          {btcMA[key]?.signal || 'N/A'}
                        </Typography>
                      </Stack>
                    </Box>
                  ) : (
                    <Alert severity="warning" key={key} sx={{ mb: 1 }}>
                      {t('market.dataUnavailableFor', { label })}
                    </Alert>
                  )
                ))}
                </Stack>
              </CardContent>
            </Card>

            {/* Fear & Greed et Halving */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              {/* Fear & Greed */}
              <Card sx={{ backgroundColor: androidColors.cardBackground, border: `1px solid ${androidColors.borderColor}`, borderRadius: '12px', flex: 1 }}>
                <CardContent>
                  <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: androidColors.textColor, mb: 1 }}>
                    {t('market.fearGreed')}
                    </Typography>
                  <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
                    {t('market.fearGreedDesc')}
                    </Typography>
                  </Box>
                  {fear_and_greed.value !== undefined && fear_and_greed.value_classification ? (
                    <>
                      <Box sx={{ 
                        backgroundColor: `${getFearGreedColor(Number(fear_and_greed.value))}10`,
                        borderRadius: '12px',
                        p: 3,
                        mb: 3,
                        border: `1px solid ${getFearGreedColor(Number(fear_and_greed.value))}30`,
                        textAlign: 'center'
                      }}>
                        <Typography
                          variant="h3"
                          sx={{
                            color: getFearGreedColor(Number(fear_and_greed.value)),
                            fontWeight: 'bold',
                            mb: 1,
                            fontFamily: 'monospace'
                          }}
                        >
                          {fear_and_greed.value}
                        </Typography>
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            color: getFearGreedColor(Number(fear_and_greed.value)),
                            fontWeight: 'bold',
                            mb: 2
                          }}
                        >
                          {fear_and_greed.value_classification}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: androidColors.textSecondary,
                            backgroundColor: `${getFearGreedColor(Number(fear_and_greed.value))}20`,
                            display: 'inline-block',
                            px: 2,
                            py: 0.5,
                            borderRadius: '16px',
                            border: `1px solid ${getFearGreedColor(Number(fear_and_greed.value))}40`
                          }}
                        >
                          {t('market.lastUpdate')}: {fear_and_greed.last_update}
                        </Typography>
                      </Box>
                      <Stack spacing={2}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1.5,
                          borderRadius: '8px',
                          backgroundColor: `${getFearGreedColor(Number(fear_and_greed.value))}10`,
                          border: `1px solid ${getFearGreedColor(Number(fear_and_greed.value))}30`
                        }}>
                          <Typography variant="body1" sx={{ color: androidColors.textColor, fontWeight: 500 }}>
                            {t('market.currentSentiment')}
                          </Typography>
                          <Typography sx={{ color: getFearGreedColor(Number(fear_and_greed.value)), fontWeight: 500 }}>
                            {fear_and_greed.value >= 75 ? 'Extrême Greed' : 
                              fear_and_greed.value >= 50 ? 'Greed' :
                              fear_and_greed.value >= 25 ? 'Fear' : 'Extrême Fear'}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1.5,
                          borderRadius: '8px',
                          backgroundColor: `${getFearGreedColor(Number(fear_and_greed.value))}10`,
                          border: `1px solid ${getFearGreedColor(Number(fear_and_greed.value))}30`
                        }}>
                          <Typography variant="body1" sx={{ color: androidColors.textColor, fontWeight: 500 }}>
                            {t('market.change24h')}
                          </Typography>
                          <Typography sx={{ 
                            color: getVariationColor(fear_and_greed.variation_24h),
                            fontWeight: 500,
                            fontFamily: 'monospace'
                          }}>
                            {fear_and_greed.variation_24h}
                          </Typography>
                        </Box>
                      </Stack>
                    </>
                  ) : (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    {t('market.fgUnavailable')}
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Compte à rebours Halving */}
              <Card sx={{ backgroundColor: androidColors.cardBackground, border: `1px solid ${androidColors.borderColor}`, borderRadius: '12px', flex: 1 }}>
                <CardContent>
                  <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: androidColors.textColor, mb: 1 }}>
                    {t('market.nextHalving')}
                    </Typography>
                  <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
                    {t('market.nextHalvingDesc')}
                    </Typography>
                  </Box>
                  {halvingDate && next_halving.est_date && next_halving.current_block && next_halving.next_halving_block && next_halving.blocks_remaining ? (
                    <>
                      <Box sx={{ 
                        backgroundColor: `${androidColors.primaryGold}10`,
                        borderRadius: '12px',
                        p: 3,
                        mb: 3,
                        border: `1px solid ${androidColors.primaryGold}30`,
                        textAlign: 'center'
                      }}>
                        <Typography
                          variant="h3"
                          sx={{
                            color: androidColors.primaryGold,
                            fontWeight: 'bold',
                            mb: 1,
                            fontFamily: 'monospace'
                          }}
                        >
                          {timeToHalving}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: androidColors.textSecondary,
                            backgroundColor: `${androidColors.primaryGold}20`,
                            display: 'inline-block',
                            px: 2,
                            py: 0.5,
                            borderRadius: '16px',
                            border: `1px solid ${androidColors.primaryGold}40`
                          }}
                        >
                          {new Date(next_halving.est_date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : undefined, { day: '2-digit', month: 'long', year: 'numeric' })}
                        </Typography>
                      </Box>
                      <Stack spacing={2}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1.5,
                          borderRadius: '8px',
                          backgroundColor: `${androidColors.primaryGold}10`,
                          border: `1px solid ${androidColors.primaryGold}30`
                        }}>
                          <Typography variant="body1" sx={{ color: androidColors.textColor, fontWeight: 500 }}>
                            {t('market.currentBlock')}
                          </Typography>
                          <Typography sx={{ color: androidColors.primaryGold, fontWeight: 500, fontFamily: 'monospace' }}>
                            {next_halving.current_block}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1.5,
                          borderRadius: '8px',
                          backgroundColor: `${androidColors.primaryGold}10`,
                          border: `1px solid ${androidColors.primaryGold}30`
                        }}>
                          <Typography variant="body1" sx={{ color: androidColors.textColor, fontWeight: 500 }}>
                            {t('market.halvingBlock')}
                          </Typography>
                          <Typography sx={{ color: androidColors.primaryGold, fontWeight: 500, fontFamily: 'monospace' }}>
                            {next_halving.next_halving_block}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1.5,
                          borderRadius: '8px',
                          backgroundColor: `${androidColors.primaryGold}10`,
                          border: `1px solid ${androidColors.primaryGold}30`
                        }}>
                          <Typography variant="body1" sx={{ color: androidColors.textColor, fontWeight: 500 }}>
                            {t('market.blocksRemaining')}
                          </Typography>
                          <Typography sx={{ color: androidColors.primaryGold, fontWeight: 500, fontFamily: 'monospace' }}>
                            {next_halving.blocks_remaining}
                          </Typography>
                        </Box>
                      </Stack>
                    </>
                  ) : (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      {t('market.halvingUnavailable')}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Stack>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>{t('market.warningTitle')}</strong> {t('market.warningText')}
              </Typography>
            </Alert>
          </Stack>
      </Box>
    </Layout>
  );
};

export default Market; 